import React, { useState, useEffect, Fragment, useMemo } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import PortalMenu from "../Common/PortalMenu";
import AppPagination from "../Common/AppPagination";
import { Row, Col, Card, Table, Badge, Dropdown, Modal, Button, Form } from "react-bootstrap";
import api, { getFileUrl } from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import swal from "sweetalert";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useRef } from "react";
import FilePreview from "../Common/FilePreview";
import usePermission from "../../../hooks/usePermission";
import SignatureCanvas from "react-signature-canvas";
import { useSelector } from "react-redux";


const DocumentList = () => {
 const [searchParams] = useSearchParams();
 const debounceRef = useRef(null);
 const { role, isAdmin, hasPermission } = usePermission();
 const canUploadDocument = isAdmin || !["client", "po"].includes((role || "").toLowerCase());

 // Profile signature support
 const userDetails = useSelector((state) => state.auth?.userDetails);
 const profileSignatureUrl = userDetails?.signature ? getFileUrl(userDetails.signature) : null;
 const [signMode, setSignMode] = useState("profile"); // "profile" | "draw"

 const [projects, setProjects] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);

 const [documentTypes, setDocumentTypes] = useState([]);

 const [documents, setDocuments] = useState([]);
 const [loading, setLoading] = useState(false);
 const [showModal, setShowModal] = useState(false);
 const [selectedDoc, setSelectedDoc] = useState(null);
 const [showStatusModal, setShowStatusModal] = useState(false);
 const [statusAction, setStatusAction] = useState("");
 const [statusRemarks, setStatusRemarks] = useState("");
 const [statusDocId, setStatusDocId] = useState(null);
 const [statusSaving, setStatusSaving] = useState(false);
 const approvalSignatureRef = useRef(null);
 const [pagination, setPagination] = useState({
 currentPage: 1,
 totalPages: 1,
 totalCount: 0,
 hasNext: false,
 hasPrev: false,
 });
 const [filters, setFilters] = usePersistentFilters("documents_filters", {
 search: "",
 project: "",
 workOrder: "",
 documentType: "",
 status: "",
 fromDate: "",
 toDate: "",
 page: 1,
 limit: 10,
 });

 useEffect(() => {
 if (debounceRef.current) clearTimeout(debounceRef.current);

 debounceRef.current = setTimeout(() => {
 fetchDocuments();
 }, 400); // 400ms delay

 return () => clearTimeout(debounceRef.current);
 }, [
 filters.search,
 filters.workOrder,
 filters.documentType,
 filters.status,
 filters.fromDate,
 filters.toDate,
 filters.page,
 filters.limit,
 ]);
 const buildFileUrl = (fileUrl) => {
 if (!fileUrl) return "#";

 // If already full URL, return as is
 if (fileUrl.startsWith("http")) {
 return fileUrl;
 }

 const base =
 (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

 return `${base}${fileUrl.startsWith("/") ? "" : "/"}${fileUrl}`;
 };
 const handlePageChange = (page) => {
 setFilters(prev => ({ ...prev, page: page }));
 };


  // SYNC SEARCHPARAMS TO FILTERS
  useEffect(() => {
    const proj = searchParams.get("project") || "";
    const wo = searchParams.get("workOrder") || "";
    const docType = searchParams.get("documentType") || "";
    if (proj || wo || docType) {
      setFilters((prev) => ({
        ...prev,
        ...(proj && { project: proj }),
        ...(wo && { workOrder: wo }),
        ...(docType && { documentType: docType }),
        page: 1,
      }));
    }
  }, [searchParams]);
  const loadProjectsAndWOs = async () => {
    try {
      const [projRes, woRes] = await Promise.all([
        api.get("/projects?limit=1000"),
        api.get("/work-orders?limit=1000")
      ]);
      if (projRes.data.success) setProjects(projRes.data.data || []);
      if (woRes.data.success) setWorkOrders(woRes.data.data || []);
    } catch (err) {
      console.error("Failed to load projects/work orders", err);
    }
  };

  useEffect(() => {
    loadProjectsAndWOs();
  }, []);

  const filteredWorkOrders = useMemo(() => {
    if (!filters.project) return workOrders;
    return workOrders.filter((wo) => {
      const pId = wo.project?._id || wo.project;
      return String(pId) === String(filters.project);
    });
  }, [workOrders, filters.project]);

  const handleProjectFilterChange = (val) => {
    setFilters((prev) => {
      const next = { ...prev, project: val, page: 1 };
      if (val && prev.workOrder) {
        const currentWo = workOrders.find((w) => String(w._id) === String(prev.workOrder));
        const currentWoProjId = currentWo?.project?._id || currentWo?.project;
        if (currentWoProjId && String(currentWoProjId) !== String(val)) {
          next.workOrder = "";
        }
      }
      return next;
    });
  };

  const handleWorkOrderFilterChange = (val) => {
    setFilters((prev) => {
      const next = { ...prev, workOrder: val, page: 1 };
      if (val) {
        const selectedWo = workOrders.find((w) => String(w._id) === String(val));
        const autoProjId = selectedWo?.project?._id || selectedWo?.project;
        if (autoProjId) {
          next.project = String(autoProjId);
        }
      }
      return next;
    });
  };
 const fetchDocumentTypes = async () => {
 try {
 const res = await api.get("/document-types"); // master API
 if (res.data.success) {
 setDocumentTypes(res.data.data || []);
 }
 } catch (err) {
 console.error("Failed to load document types", err);
 }
 };
 useEffect(() => {
 fetchDocumentTypes();
 }, []);

 const fetchDocuments = async () => {
 try {
 setLoading(true);

 const params = new URLSearchParams();

 if (filters.search) params.append("search", filters.search);
 if (filters.project) params.append("project", filters.project);
 if (filters.workOrder) params.append("workOrder", filters.workOrder);

 if (filters.status) params.append("status", filters.status);
 if (filters.fromDate) params.append("fromDate", filters.fromDate);
 if (filters.toDate) params.append("toDate", filters.toDate);
 if (filters.documentType)
 params.append("documentType", filters.documentType);

 params.append("page", filters.page);
 params.append("limit", filters.limit);

 const response = await api.get(`/documents?${params.toString()}`);

 if (response.data.success) {
 setDocuments(response.data.data || []);
 setPagination(response.data.pagination);
 } else {
 setDocuments([]);
 }
 } catch (error) {
 console.error("Error fetching documents:", error);
 setDocuments([]);
 } finally {
 setLoading(false);
 }
 };

 const handleView = async (doc) => {
 try {
 const response = await api.get(`/documents/${doc._id}`);
 if (response.data.success) {
 setSelectedDoc(response.data.data);
 setShowModal(true);
 }
 } catch (error) {
 console.error("Error fetching document details:", error);
 swal("Error", "Failed to load document details.", "error");
 }
 };
 useEffect(() => {
 const project = searchParams.get("project");
 const workOrder = searchParams.get("workOrder");
 const documentTypeName = searchParams.get("documentType");

 let documentTypeId = "";

 if (documentTypeName && documentTypes.length) {
 const match = documentTypes.find(
 (d) => d.name === documentTypeName
 );
 documentTypeId = match?._id || "";
 }

 setFilters((prev) => ({
 ...prev,
 project: project || "",
 workOrder: workOrder || "",
 documentType: documentTypeId,
 page: 1,
 }));
 }, [searchParams, documentTypes]);


 const openStatusModal = (id, status) => {
 setStatusDocId(id);
 setStatusAction(status);
 setStatusRemarks("");
 setShowStatusModal(true);
 setSignMode(profileSignatureUrl ? "profile" : "draw");
 setTimeout(() => approvalSignatureRef.current?.clear(), 0);
 };

 const closeStatusModal = () => {
 setShowStatusModal(false);
 setStatusDocId(null);
 setStatusAction("");
 setStatusRemarks("");
 approvalSignatureRef.current?.clear();
 };

 const handleStatusChange = async () => {
 if (!statusDocId || !statusAction) return;
 const remarks = statusRemarks.trim();
 if (!remarks) {
 swal("Warning", "Please enter remarks before continuing.", "warning");
 return;
 }

 if (statusAction === "Approved") {
 if (signMode === "draw" && approvalSignatureRef.current?.isEmpty?.()) {
 swal("Warning", "Please sign before approving.", "warning");
 return;
 }
 if (signMode === "profile" && !profileSignatureUrl) {
 swal("Warning", "No profile signature found. Please draw your signature or upload one in Profile Settings.", "warning");
 return;
 }
 }

 try {
 setStatusSaving(true);
 const formData = new FormData();
 formData.append("status", statusAction);
 formData.append("remarks", remarks);

 if (statusAction === "Approved") {
 if (signMode === "draw" && approvalSignatureRef.current) {
 const dataURL = approvalSignatureRef.current.toDataURL("image/png");
 const arr = dataURL.split(",");
 const mime = arr[0].match(/:(.*?);/)[1];
 const bstr = atob(arr[1]);
 let n = bstr.length;
 const u8arr = new Uint8Array(n);
 while (n--) u8arr[n] = bstr.charCodeAt(n);
 const blob = new Blob([u8arr], { type: mime });
 const file = new File([blob], "document-signature.png", { type: "image/png" });
 formData.append("signature", file);
 } else if (signMode === "profile" && profileSignatureUrl) {
 // Reuse existing profile signature path — no file download needed
 const sigPath = userDetails?.signature;
 if (sigPath) {
 formData.append("existingSignaturePath", sigPath);
 } else {
 swal("Error", "Profile signature path not found.", "error");
 setStatusSaving(false);
 return;
 }
 } }

 const response = await api.patch(`/documents/${statusDocId}`, formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });

 if (response.data.success) {
 swal("Success", `Document ${statusAction.toLowerCase()} successfully!`, "success");
 closeStatusModal();
 fetchDocuments(pagination.currentPage);
 } else {
 swal("Error", "Failed to update status", "error");
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || "Failed to update document status", "error");
 } finally {
 setStatusSaving(false);
 }
 };

 const handleDelete = async (id) => {
 swal({
 title: "Delete Document?",
 text: "Are you sure you want to delete this document?",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 }).then(async (confirmed) => {
 if (!confirmed) return;

 try {
 const response = await api.delete(`/documents/${id}`);
 if (response.data.success) {
 swal("Deleted!", "Document deleted successfully", "success");
 fetchDocuments(pagination.currentPage);
 } else {
 swal("Error", "Failed to delete document", "error");
 }
 } catch (error) {
 console.error("Error deleting document:", error);
 swal("Error", "Failed to delete document", "error");
 }
 });
 };

 const formatDate = (dateString) => {
 if (!dateString) return "—";
 const date = new Date(dateString);
 return date.toLocaleDateString("en-US", {
 year: "numeric",
 month: "short",
 day: "numeric",
 });
 };

 const getDocumentEffectiveStatus = (doc) => {
 const status = String(doc?.status || "").trim();
 const hasApprovalTrail = Boolean(doc?.approvedBy && doc?.approvalDate);

 if (status === "Approved" || hasApprovalTrail) return "Approved";
 if (status === "Rejected") return "Rejected";
 if (status === "Submitted") return "Submitted";
 return "Pending Approval";
 };

 const getStatusBadge = (status) => {
 switch (String(status || "")) {
 case "Approved":
 return "success";
 case "Pending Approval":
 return "warning";
 case "Rejected":
 return "danger";
 case "Submitted":
 return "info";
 default:
 return "secondary";
 }
 };

 const getDocumentVersionHistory = (doc) => {
 if (!doc) return [];

 const history = Array.isArray(doc.revisionHistory) ? doc.revisionHistory : [];
 const entries = history
 .map((item) => ({
 version: item.version || doc.version || 1,
 fileName: item.fileName || doc.fileName,
 fileUrl: item.fileUrl,
 uploadedAt: item.uploadedAt || doc.updatedAt || doc.submissionDate,
 uploadedBy: item.uploadedBy?.name || item.uploadedBy?.email || "Unknown",
 isCurrent:
 String(item.version || "") === String(doc.version || "") &&
 String(item.fileUrl || "") === String(doc.fileUrl || ""),
 }))
 .filter((item) => item.fileUrl)
 .sort((a, b) => {
 const av = Number(a.version || 0);
 const bv = Number(b.version || 0);
 if (av !== bv) return bv - av;
 return new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0);
 });

 if (!entries.length) {
 entries.push({
 version: doc.version || 1,
 fileName: doc.fileName,
 fileUrl: doc.fileUrl,
 uploadedAt: doc.updatedAt || doc.submissionDate,
 uploadedBy: doc.submittedBy?.name || doc.submittedBy?.email || "Unknown",
 isCurrent: true,
 });
 }

 return entries;
 };

 const svgMenu = (
 <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
 <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
 <circle fill="#000000" cx="5" cy="12" r="2"></circle>
 <circle fill="#000000" cx="12" cy="12" r="2"></circle>
 <circle fill="#000000" cx="19" cy="12" r="2"></circle>
 </g>
 </svg>
 );

 return (
 <Fragment>
 <PageTitle
 activeMenu="Documents"
 motherMenu="Document & Approval Management"
 />
 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title>Document List</Card.Title>
 {canUploadDocument && (
 <Link
 to={
 filters.workOrder || filters.documentType
 ? `/documents/upload?${filters.workOrder ? `workOrder=${filters.workOrder}&` : ""}${filters.documentType ? `documentType=${filters.documentType}` : ""}`
 : "/documents/upload"
 }
 className="btn btn-primary"
 >
 + Upload Document
 </Link>
 )}
 </Card.Header>

 <div className="d-flex flex-wrap align-items-end gap-2 px-4 py-3 border-bottom">
 <div className="d-flex flex-column" style={{ width: "160px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Search File</label>
 <input
 type="text"
 className="form-control form-control-sm"
 placeholder="Search file..."
 value={filters.search}
 onChange={(e) =>
 setFilters((prev) => ({
 ...prev,
 search: e.target.value,
 }))
 }
 />
 </div>
            <div className="d-flex flex-column" style={{ width: "150px" }}>
              <label className="text-muted mb-1 fs-12 font-weight-bold">Project</label>
              <select
                className="form-select form-select-sm form-select"
                value={filters.project}
                onChange={(e) => handleProjectFilterChange(e.target.value)}
              >
                <option value="">All Projects</option>
                {projects.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.projectName || p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="d-flex flex-column" style={{ width: "150px" }}>
              <label className="text-muted mb-1 fs-12 font-weight-bold">Work Order</label>
              <select
                className="form-select form-select-sm form-select"
                value={filters.workOrder}
                onChange={(e) => handleWorkOrderFilterChange(e.target.value)}
              >
                <option value="">All Work Orders</option>
                {filteredWorkOrders.map((wo) => (
                  <option key={wo._id} value={wo._id}>
                    {wo.woNo || wo.woNumber}{wo.label ? ` – ${wo.label}` : ""}{!filters.project && (wo.project?.projectName || wo.project?.name) ? ` (${wo.project.projectName || wo.project.name})` : ""}
                  </option>
                ))}
              </select>
            </div>
 <div className="d-flex flex-column" style={{ width: "130px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Type</label>
 <select
 className="form-select form-select-sm form-select"
 value={filters.documentType}
 onChange={(e) =>
 setFilters((prev) => ({
 ...prev,
 documentType: e.target.value,
 page: 1,
 }))
 }
 >
 <option value="">All Types</option>
 {documentTypes.map((d) => (
 <option key={d._id} value={d._id}>
 {d.name}
 </option>
 ))}
 </select>
 </div>
 <div className="d-flex flex-column" style={{ width: "130px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Status</label>
 <select
 className="form-select form-select-sm form-select"
 value={filters.status}
 onChange={(e) =>
 setFilters((prev) => ({
 ...prev,
 status: e.target.value,
 page: 1,
 }))
 }
 >
 <option value="">All Status</option>
 <option value="Submitted">Submitted</option>
 <option value="Pending Approval">Pending Approval</option>
 <option value="Approved">Approved</option>
 <option value="Rejected">Rejected</option>
 </select>
 </div>
 <div className="d-flex flex-column" style={{ width: "135px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">From Date</label>
 <input
 type="date"
 className="form-control form-control-sm"
 value={filters.fromDate}
 onChange={(e) =>
 setFilters((prev) => ({
 ...prev,
 fromDate: e.target.value,
 page: 1,
 }))
 }
 />
 </div>
 <div className="d-flex flex-column" style={{ width: "135px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">To Date</label>
 <input
 type="date"
 className="form-control form-control-sm"
 value={filters.toDate}
 onChange={(e) =>
 setFilters((prev) => ({
 ...prev,
 toDate: e.target.value,
 page: 1,
 }))
 }
 />
 </div>
 <div className="ms-auto d-flex flex-column" style={{ width: "65px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Show</label>
 <select
 className="form-select form-select-sm form-select"
 value={filters.limit}
 onChange={(e) =>
 setFilters((prev) => ({
 ...prev,
 limit: Number(e.target.value),
 page: 1,
 }))
 }
 >
 <option value={10}>10</option>
 <option value={25}>25</option>
 <option value={50}>50</option>
 </select>
 </div>
 </div>

 <Card.Body>
 {loading ? (
 <div className="text-center py-5">
 <div
 className="spinner-border text-primary"
 role="status"
 ></div>
 <p className="mt-3">Loading documents...</p>
 </div>
 ) : documents.length === 0 ? (
 <div className="text-center py-5">
 <p className="text-muted">
 No documents found. Upload one to get started!
 </p>
 {canUploadDocument && (
 <Link to="/documents/upload" className="btn btn-primary">
 + Upload Document
 </Link>
 )}
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover>
 <thead>
 <tr>
 <th>#</th>
 <th>Type</th>
 <th>Work Order</th>
 <th>File Name</th>
 <th>Submitted By</th>
 <th>Date</th>
 <th>Status</th>
 <th>Version</th>
 <th>Actions</th>
 </tr>
 </thead>
 <tbody>
 {documents.map((doc, i) => (
 <tr key={doc._id}>
 <td>{(filters.page - 1) * filters.limit + i + 1}</td>
 <td>{doc.documentType?.name || "N/A"}</td>
 <td>
 {doc.workOrder
 ? `${doc.workOrder.woNo || doc.workOrder.woNumber || "—"}`
 : "—"}
 </td>

 <td>
 <a
 href={buildFileUrl(doc.fileUrl)}

 target="_blank"
 rel="noopener noreferrer"
 className="text-primary fw-bold"
 >
 {doc.fileName}
 </a>
 </td>
 <td>{doc.submittedBy?.name || "Unknown"}</td>
 <td>{formatDate(doc.submissionDate)}</td>
 <td>
 <Badge bg={getStatusBadge(getDocumentEffectiveStatus(doc))}>
 {getDocumentEffectiveStatus(doc)}
 </Badge>
 </td>
 <td>v{doc.version}</td>

 <td>
 <Dropdown className="ms-auto">
 <Dropdown.Toggle
 variant=""
 as="div"
 className="btn-link i-false"
 >
 {svgMenu}
 </Dropdown.Toggle>

 <Dropdown.Menu align="end" as={PortalMenu}>
 <Dropdown.Item onClick={() => handleView(doc)}>
 <i className="fas fa-eye me-2"></i>View
 Details
 </Dropdown.Item>

 <Dropdown.Item
 href={getFileUrl(doc.fileUrl)}
 target="_blank"
 rel="noopener noreferrer"
 >
 <i className="fas fa-file-alt me-2 text-info"></i>
 View File
 </Dropdown.Item>

 <Dropdown.Item
 as={Link}
 to={
 filters.workOrder || filters.documentType
 ? `/documents/edit/${doc._id}?${filters.workOrder ? `workOrder=${filters.workOrder}&` : ""}${filters.documentType ? `documentType=${filters.documentType}` : ""}`
 : `/documents/edit/${doc._id}`
 }
 >
 <i className="fas fa-edit me-2"></i>Edit
 </Dropdown.Item>

 {getDocumentEffectiveStatus(doc) === "Pending Approval" && (
 <>
 {(isAdmin || hasPermission("approve:documents")) && (
 <Dropdown.Item
 onClick={() => openStatusModal(doc._id, "Approved")}
 className="text-success"
 >
 <i className="fas fa-check me-2"></i>
 Approve
 </Dropdown.Item>
 )}

 {(isAdmin || hasPermission("reject:documents")) && (
 <Dropdown.Item
 onClick={() => openStatusModal(doc._id, "Rejected")}
 className="text-danger"
 >
 <i className="fas fa-times me-2"></i>
 Reject
 </Dropdown.Item>
 )}
 </>
 )}

 <Dropdown.Divider />

 {(isAdmin || hasPermission("delete:documents")) && (
 <Dropdown.Item
 onClick={() => handleDelete(doc._id)}
 className="text-danger"
 >
 <i className="fas fa-trash me-2"></i>Delete
 </Dropdown.Item>
 )}
 </Dropdown.Menu>
 </Dropdown>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 {/* Pagination */}
 <AppPagination
 currentPage={filters.page}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={documents.length}
 limit={filters.limit}
 onPageChange={handlePageChange}
 />
 </div>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 <Modal show={showStatusModal} onHide={closeStatusModal} centered>
 <Modal.Header closeButton>
 <Modal.Title>{statusAction ? `${statusAction} Document` : "Update Document Status"}</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 <Form.Group className="mb-3">
 <Form.Label className="fw-bold">Remarks</Form.Label>
 <Form.Control
 as="textarea"
 rows={4}
 value={statusRemarks}
 onChange={(e) => setStatusRemarks(e.target.value)}
 placeholder={`Enter remarks for ${statusAction ? statusAction.toLowerCase() : "this action"}`}
 />
 </Form.Group>

 {statusAction === "Approved" && (
 <Form.Group className="mt-1">
 <Form.Label className="fw-bold d-block mb-2">Signature Type</Form.Label>

 {/* Radio buttons */}
 <div className="d-flex gap-4 mb-3">
 <Form.Check
 type="radio"
 id="doc-sig-profile"
 name="docSignMode"
 label="Use Profile Signature"
 value="profile"
 checked={signMode === "profile"}
 onChange={() => setSignMode("profile")}
 />
 <Form.Check
 type="radio"
 id="doc-sig-draw"
 name="docSignMode"
 label="Draw Signature"
 value="draw"
 checked={signMode === "draw"}
 onChange={() => setSignMode("draw")}
 />
 </div>

 {/* Profile signature preview */}
 {signMode === "profile" && (
 profileSignatureUrl ? (
 <div className="text-center mb-2">
 <div className="border rounded p-2 bg-light d-inline-block">
 <img
 src={profileSignatureUrl}
 alt="Profile Signature"
 style={{ maxWidth: "260px", maxHeight: "90px", objectFit: "contain" }}
 />
 </div>
 <p className="text-muted small mt-1 mb-0">Your saved profile signature</p>
 </div>
 ) : (
 <div className="alert alert-warning py-2 mb-2">
 <i className="fas fa-exclamation-triangle me-2"></i>
 No profile signature found.{" "}
 <a href="/profile" target="_blank" rel="noreferrer">Upload in Profile Settings</a>
 {" "}or select <strong>Draw Signature</strong>.
 </div>
 )
 )}

 {/* Draw canvas */}
 {signMode === "draw" && (
 <>
 <div className="border rounded bg-light p-2">
 <SignatureCanvas
 ref={approvalSignatureRef}
 penColor="black"
 canvasProps={{ width: 520, height: 150, className: "w-100" }}
 />
 </div>
 <div className="d-flex justify-content-between align-items-center mt-1">
 <small className="text-muted">Sign inside the box above.</small>
 <Button
 type="button"
 variant="link"
 size="sm"
 className="p-0 text-secondary"
 onClick={() => approvalSignatureRef.current?.clear()}
 >
 Clear
 </Button>
 </div>
 </>
 )}
 </Form.Group>
 )}
 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={closeStatusModal} disabled={statusSaving}>
 Cancel
 </Button>
 <Button
 variant={statusAction === "Approved" ? "success" : "danger"}
 onClick={handleStatusChange}
 disabled={statusSaving || (statusAction === "Approved" && signMode === "profile" && !profileSignatureUrl)}
 >
 {statusSaving ? "Saving..." : statusAction || "Save"}
 </Button>
 </Modal.Footer>
 </Modal>

 {/* Modal */}
 <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
 <Modal.Header closeButton>
 <Modal.Title>Document Details</Modal.Title>
 </Modal.Header>

 <Modal.Body>
 {selectedDoc && (
 <>
 <Row>
 <Col md={6}>
 <p><strong>Type:</strong> {selectedDoc.documentType?.name || "N/A"}</p>
 <p>
 <strong>Work Order:</strong>{" "}
 {selectedDoc.workOrder?.woNo || selectedDoc.workOrder?.woNumber || "—"}
 </p>
 <p>
 <strong>File Name:</strong> {selectedDoc.fileName}
 </p>
 <p>
 <strong>Version:</strong> v{selectedDoc.version}
 </p>
 </Col>

 <Col md={6}>
 <p>
 <strong>Submitted By:</strong>{" "}
 {selectedDoc.submittedBy?.name || "Unknown"}
 </p>
 <p>
 <strong>Submission Date:</strong>{" "}
 {formatDate(selectedDoc.submissionDate)}
 </p>
 <p>
 <strong>Status:</strong>{" "}
 <Badge bg={getStatusBadge(getDocumentEffectiveStatus(selectedDoc))}>
 {getDocumentEffectiveStatus(selectedDoc)}
 </Badge>
 </p>
 </Col>
 </Row>

 <hr />
 <h5>File</h5>
 <FilePreview
 file={{
 fileName: selectedDoc.fileName,
 fileUrl: selectedDoc.fileUrl
 }}
 label={`Current version v${selectedDoc.version}`}
 onRemove={async () => {
 const confirm = await swal({
 title: "Delete Document?",
 text: "This will permanently delete this document and its file.",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });
 if (!confirm) return;
 try {
 const res = await api.delete(`/documents/${selectedDoc._id}`);
 if (res.data.success) {
 swal("Deleted!", "Document deleted successfully", "success");
 setShowModal(false);
 fetchDocuments();
 }
 } catch (err) {
 swal("Error", "Failed to delete document", "error");
 }
 }}
 />

 <hr />
 <h5>Version History</h5>
 {getDocumentVersionHistory(selectedDoc).length ? (
 <div className="table-responsive">
 <Table bordered hover size="sm" className="align-middle">
 <thead>
 <tr>
 <th style={{ width: "12%" }}>Version</th>
 <th style={{ width: "26%" }}>Uploaded On</th>
 <th style={{ width: "24%" }}>Uploaded By</th>
 <th>File</th>
 <th style={{ width: "10%" }}>Current</th>
 </tr>
 </thead>
 <tbody>
 {getDocumentVersionHistory(selectedDoc).map((entry, index) => (
 <tr key={`${entry.version}-${entry.fileUrl || index}`}>
 <td>v{entry.version}</td>
 <td>{formatDate(entry.uploadedAt)}</td>
 <td>{entry.uploadedBy}</td>
 <td>
 <a
 href={buildFileUrl(entry.fileUrl)}
 target="_blank"
 rel="noopener noreferrer"
 className="text-primary fw-bold"
 >
 {entry.fileName || `Version ${entry.version}`}
 </a>
 </td>
 <td>
 {entry.isCurrent ? (
 <Badge bg="success">Current</Badge>
 ) : (
 <Badge bg="secondary">Old</Badge>
 )}
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 ) : (
 <p className="text-muted mb-0">No version history available.</p>
 )}

 {getDocumentEffectiveStatus(selectedDoc) === "Approved" && selectedDoc.approvedBy && (
 <>
 <hr />
 <h5>Approval Information</h5>
 <p>
 <strong>Approved By:</strong> {selectedDoc.approvedBy.name}
 </p>
 <p>
 <strong>Approval Date:</strong>{" "}
 {formatDate(selectedDoc.approvalDate)}
 </p>
 </>
 )}

 {selectedDoc.signature?.fileUrl && (
 <>
 <hr />
 <h5>Signature Preview</h5>
 <Row className="mt-2">
 <Col md={4} lg={3} xl={3}>
 <div className="border rounded bg-white p-2 d-inline-block">
 <img
 src={buildFileUrl(selectedDoc.signature.fileUrl)}
 alt={selectedDoc.signature.fileName || "Signature"}
 style={{
 width: "100%",
 maxWidth: "220px",
 maxHeight: "120px",
 objectFit: "contain",
 }}
 />
 </div>
 </Col>
 </Row>
 </>
 )}

 {selectedDoc.remarks && (
 <Row className="mt-3">
 <Col>
 <p>
 <strong>Remarks:</strong>
 </p>
 <p className="text-muted">{selectedDoc.remarks}</p>
 </Col>
 </Row>
 )}

 <Row className="mt-3">
 <Col>
 <a
 href={buildFileUrl(selectedDoc.fileUrl)}
 target="_blank"
 rel="noopener noreferrer"
 className="btn btn-primary"
 >
 <i className="fas fa-download me-2"></i>Download File
 </a>
 </Col>
 </Row>
 </>
 )}
 </Modal.Body>

 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowModal(false)}>
 Close
 </Button>
 </Modal.Footer>
 </Modal>
 </Fragment>
 );
};

export default DocumentList;
